/* ─────────────────────────────────────────────────────────────────
   MLW Quiz Klaviyo — Direct Client API subscribe
   ─────────────────────────────────────────────────────────────────
   Minimal, brand-agnostic Klaviyo integration. Creates/updates a
   profile and subscribes it to a list via Klaviyo's Client API v3.

   Paired with:
     • snippets/quiz-klaviyo-init.liquid — bootstraps init() and
       listens for the mlw-quiz:email-submitted event to call
       subscribe() on the user's behalf.
     • sections/mlw-quiz-klaviyo.liquid — schema-driven opt-in
       per-page; passes apiKey / listId / brandSlug into the snippet.

   Errors are caught and logged — subscribe() never throws into the
   quiz flow. The user always reaches the results stage.
   ───────────────────────────────────────────────────────────────── */

window.MLWQuizKlaviyo = {
  _config: {},

  init({ apiKey, listId, brandSlug, segmentPropertyName, quizIdPropertyName, quizIdValue, segmentListIds, defaultListId }) {
    this._config = {
      apiKey: apiKey,
      listId: listId,
      brandSlug: brandSlug,
      segmentPropertyName: segmentPropertyName,
      quizIdPropertyName: quizIdPropertyName,
      quizIdValue: quizIdValue,
      segmentListIds: segmentListIds || {},
      defaultListId: defaultListId || ''
    };
  },

  async subscribe({ email, firstName, quizAnswers, quizResults, segment }) {
    const config = this._config;
    const { apiKey, listId, brandSlug } = config;
    if (!apiKey || !email) return;

    // Resolve segment from arg or global stash set by the quiz.
    var resolvedSegment = segment || window.MLWQuizCurrentSegment || '';

    // Determine which list to subscribe to — segment list takes priority,
    // default is fallback only. User is added to ONE list, never both.
    var targetListId = null;
    if (resolvedSegment && config.segmentListIds && config.segmentListIds[resolvedSegment]) {
      targetListId = config.segmentListIds[resolvedSegment];
    }
    if (!targetListId && config.defaultListId) {
      targetListId = config.defaultListId;
    }
    if (!targetListId) {
      targetListId = listId;
    }
    if (!targetListId) return;

    var properties = {
      quiz_source: brandSlug || '',
      quiz_completed: true,
      quiz_answers: JSON.stringify(quizAnswers || []),
      quiz_results: JSON.stringify(quizResults || {})
    };

    // Configurable custom properties — names from schema, segment value
    // is the dynamic result of MLWQuizSegmentRouter, the quiz id value
    // is itself configurable per quiz.
    if (config.segmentPropertyName && resolvedSegment) {
      properties[config.segmentPropertyName] = resolvedSegment;
    }
    if (config.quizIdPropertyName && config.quizIdValue) {
      properties[config.quizIdPropertyName] = config.quizIdValue;
    }

    console.info('[MLWQuizKlaviyo] subscribe called', { email: email, segment: resolvedSegment, targetListId: targetListId });

    function checkResponse(label) {
      return function (response) {
        if (!response.ok) {
          response.text().then(function (body) {
            console.error('[MLWQuizKlaviyo] ' + label + ' API error', response.status, body);
          });
        } else {
          console.info('[MLWQuizKlaviyo] ' + label + ' API success', response.status);
        }
        return response;
      };
    }

    try {
      // Create / update profile — company_id MUST be a query param
      // (Client API auth), not in the body.
      await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(apiKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email: email,
              first_name: firstName || '',
              properties: properties
            }
          }
        })
      }).then(checkResponse('profiles'));

      // Subscribe to list — mirror properties onto the inline profile.
      // The /client/subscriptions/ endpoint implicitly handles consent;
      // do NOT add a `subscriptions` key on profile attributes (Klaviyo
      // rejects it as an invalid field on the profile resource).
      await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Quiz - ' + (brandSlug || 'unknown'),
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    first_name: firstName || '',
                    properties: properties
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: targetListId } }
            }
          }
        })
      }).then(checkResponse('subscriptions'));
    } catch (err) {
      console.warn('[MLWQuizKlaviyo] Subscribe failed:', err);
    }
  }
};
